/**
 * Loading, empty, error and notice states (spec/style-guide.md §4.5): skeletons for regions, a
 * spinner only for a named operation in progress, `Empty` for nothing to show, an Alert with Retry
 * for a failed request.
 */
import { CircleAlert, Info, RotateCw, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

/**
 * A region that is loading. Without a `label` it is a skeleton shaped like a header and a card of
 * lines; with a `label` ("Checking your Discord roles…") it names an operation in progress.
 */
export function Loading({ label, className }: { label?: string; className?: string }) {
  if (label) {
    return (
      <div
        role="status"
        className={cn('flex items-center gap-2 py-6 text-sm text-muted-foreground', className)}
      >
        <Spinner aria-hidden /> {label}
      </div>
    );
  }
  return (
    <div role="status" aria-label="Loading" className={cn('grid gap-4 py-2', className)}>
      <div className="grid gap-2">
        <Skeleton className="h-7 w-56 max-w-full" />
        <Skeleton className="h-4 w-80 max-w-full" />
      </div>
      <div className="grid gap-3 rounded-xl border p-6">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

/** Skeleton rows for a list or table region. */
export function ListSkeleton({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div role="status" aria-label="Loading" className={cn('grid gap-2', className)}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/** A form's or action's error sentence (under the form, inside a dialog). */
export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="text-sm text-destructive">
      {children}
    </p>
  );
}

/** A form's or action's result sentence. */
export function SuccessText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p role="status" className="text-sm text-success-foreground">
      {children}
    </p>
  );
}

/** A failed request in a region: what failed, the reason, and Retry. */
export function ErrorState({
  title = "Couldn't load this",
  error,
  onRetry,
  className,
}: {
  title?: ReactNode;
  error?: ReactNode;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <Alert variant="destructive" className={className}>
      <CircleAlert />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="grid gap-3">
        <span>{error ?? 'Check your connection and try again.'}</span>
        {onRetry ? (
          <span>
            <Button size="sm" variant="outline" onClick={onRetry}>
              <RotateCw /> Retry
            </Button>
          </span>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/** Nothing to show (shadcn Empty): an icon, an optional title, one line, an optional action. */
export function EmptyState({
  children,
  className,
  icon: Icon,
  title,
  action,
}: {
  children: ReactNode;
  className?: string;
  icon?: LucideIcon;
  title?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Empty className={cn('rounded-xl border border-dashed py-10', className)}>
      <EmptyHeader className="max-w-md">
        {Icon ? (
          <EmptyMedia variant="icon">
            <Icon />
          </EmptyMedia>
        ) : null}
        {title ? <EmptyTitle>{title}</EmptyTitle> : null}
        <EmptyDescription>{children}</EmptyDescription>
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </Empty>
  );
}

const ICONS = { info: Info, warning: TriangleAlert, destructive: CircleAlert };

/** A page-level notice (shadcn Alert) with an icon, an optional title and a description. */
export function Callout({
  variant = 'info',
  title,
  children,
  className,
}: {
  variant?: 'info' | 'warning' | 'destructive';
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const Icon = ICONS[variant];
  return (
    <Alert
      variant={variant === 'info' ? 'default' : variant}
      role={variant === 'info' ? 'status' : 'alert'}
      className={className}
    >
      <Icon />
      {title ? <AlertTitle>{title}</AlertTitle> : null}
      {children ? <AlertDescription>{children}</AlertDescription> : null}
    </Alert>
  );
}
